import { TextSelection } from "@tiptap/pm/state";
import { CellSelection, selectionCell, TableMap } from "@tiptap/pm/tables";

import TableStepper from "./table-stepper";

export function selectedRect(state) {
    const sel = state.selection;
    const $pos = selectionCell(state);
    const table = $pos.node(-1);
    const tableStart = $pos.start(-1);
    const map = TableMap.get(table);
    const rect =
        sel instanceof CellSelection
            ? map.rectBetween(sel.$anchorCell.pos - tableStart, sel.$headCell.pos - tableStart)
            : map.findCell($pos.pos - tableStart);
    return { ...rect, tableStart, map, table };
}

/**
 * Validates if the current cell selection can be merged.
 * Returns false if any cell partially overlaps with the selection rectangle boundaries.
 * This prevents invalid merges that would create broken tables (like L-shaped selections).
 *
 * @param {Object} state - Editor state
 * @returns {boolean} - True if the selection can be merged, false otherwise
 */
export function canMergeSelection(state) {
    const sel = state.selection;

    // Must be a cell selection with at least 2 cells
    if (!(sel instanceof CellSelection) || sel.$anchorCell.pos === sel.$headCell.pos) {
        return false;
    }

    try {
        const rect = selectedRect(state);
        const { left, right, top, bottom, map } = rect;

        // Check each cell position in the table map within the selection rectangle
        for (let row = top; row < bottom; row++) {
            for (let col = left; col < right; col++) {
                const cellPos = map.map[row * map.width + col];
                const cellRect = map.findCell(cellPos);

                // Check if the cell extends beyond the selection rectangle
                // If a cell starts before or extends after the selection boundary,
                // it means we have a partial overlap which would create an invalid merge
                if (
                    cellRect.left < left ||
                    cellRect.right > right ||
                    cellRect.top < top ||
                    cellRect.bottom > bottom
                ) {
                    return false;
                }
            }
        }

        return true;
    } catch (e) {
        return false;
    }
}

// Allows merging even if the rectangle borders intersect existing spans.
// It computes the unified rowspan/colspan based on the selected rectangle size
// and flattens inner cells accordingly.
export const mergeCellsLenient = ({ state, dispatch, editor }) => {
    const sel = state.selection;

    if (!(sel instanceof CellSelection) || sel.$anchorCell.pos === sel.$headCell.pos) {
        return false;
    }

    // Validate that the selection forms a valid rectangle without partial overlaps
    if (!canMergeSelection(state)) {
        return false;
    }

    const rect = selectedRect(state);
    const { table, left, right, top, bottom, tableStart } = rect;

    const tableJSON = JSON.parse(JSON.stringify(table.toJSON()));

    const [cellToMergeToSlot] = [
        ...new TableStepper(tableJSON, {
            row: top,
            column: left,
            tableMode: "JSON",
        }),
    ];

    if (!cellToMergeToSlot) {
        return false;
    }

    const cellToMergeTo = cellToMergeToSlot.cell;

    // Compute target spans directly from rectangle
    const targetColspan = right - left;
    const targetRowspan = bottom - top;

    // Collect all slots and their cells within rect
    const slots = [
        ...new TableStepper(tableJSON, {
            startRow: top,
            endRow: bottom - 1,
            startColumn: left,
            endColumn: right - 1,
            tableMode: "JSON",
        }),
    ];

    const cellsToDelete = [];
    for (const { cell } of slots) {
        if (cell !== cellToMergeTo) {
            if (cell.content.length > 0) {
                cellToMergeTo.content = cellToMergeTo.content.concat(cell.content);
            }
            cellsToDelete.push(cell);
        }
    }

    // Set unified spans and reset colwidth to uniform array if present
    cellToMergeTo.attrs = {
        ...cellToMergeTo.attrs,
        colspan: targetColspan,
        rowspan: targetRowspan,
    };
    if (cellToMergeTo.attrs.colwidth) {
        cellToMergeTo.attrs.colwidth = new Array(targetColspan).fill(
            cellToMergeTo.attrs.colwidth[0] || 0,
        );
    }

    // Remove merged cells from rows
    const newTableContent = tableJSON.content.map((row, rowIndex) => {
        return {
            ...row,
            content: row.content.filter((cell) => !cellsToDelete.includes(cell)),
            _originalIndex: rowIndex, // Track original row index
        };
    });

    // Identify which rows will be removed (empty rows)
    const rowsToRemove = new Set();
    newTableContent.forEach((row, idx) => {
        if (row.content.length === 0) {
            rowsToRemove.add(row._originalIndex);
        }
    });

    // Only adjust rowspans for cells that actually span across removed rows
    if (rowsToRemove.size > 0) {
        newTableContent.forEach((row, rowIdx) => {
            if (row.content.length > 0) {
                row.content = row.content.map((cell) => {
                    const currentRowspan = cell.attrs.rowspan || 1;

                    // Only process cells with rowspan > 1
                    if (currentRowspan > 1) {
                        // Count how many of the rows this cell spans into are being removed
                        let removedRowsInSpan = 0;
                        const cellStartRow = row._originalIndex;

                        for (let spanIdx = 1; spanIdx < currentRowspan; spanIdx++) {
                            const spannedRowIndex = cellStartRow + spanIdx;
                            if (rowsToRemove.has(spannedRowIndex)) {
                                removedRowsInSpan++;
                            }
                        }

                        // Reduce rowspan by the number of removed rows it spans
                        if (removedRowsInSpan > 0) {
                            const newRowspan = Math.max(1, currentRowspan - removedRowsInSpan);
                            return {
                                ...cell,
                                attrs: {
                                    ...cell.attrs,
                                    rowspan: newRowspan === 1 ? undefined : newRowspan,
                                },
                            };
                        }
                    }

                    return cell;
                });
            }
        });
    }

    // Remove the temporary _originalIndex property and filter out empty rows
    const newTableJSON = {
        ...tableJSON,
        content: newTableContent
            .filter((row) => row.content.length > 0)
            .map((row) => {
                const { _originalIndex, ...cleanRow } = row;
                return cleanRow;
            }),
    };

    const newTableNode = editor.schema.nodeFromJSON(newTableJSON);
    if (dispatch) {
        let tr = state.tr;
        tr.delete(tableStart - 1, tableStart + table.nodeSize - 1);
        tr.insert(tableStart - 1, newTableNode);
        tr.setSelection(TextSelection.create(tr.doc, state.selection.from));
        dispatch(tr);
    }

    return true;
};
